function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import GoogleMapReact, { fitBounds } from 'google-map-react';
import { Loader } from '@domain-group/fe-blueprint-ui/loader';
import { InfoIcon } from '@domain-group/fe-co-icon';
import { withSendEvent } from '@domain-group/fe-co-allhomes-analytics';
import { mapUtils } from '../utils';
import { MAP_SEARCH_PROPS, BOUNDS_PROPS } from '../proptypes/map-search';
import SingleMarker from './map/single-marker';
import PopupWindow from './map/popup-window';
import * as styles from './allhomes-listing-map-search.style';
import { jsx as ___EmotionJSX } from "@emotion/core";
var AUTO_MIN_ZOOM = 8;
var AUTO_MAX_ZOOM = 18;
var DEFAULT_ZOOM_LEVEL = 16;

// We have several delays in place for debounced functions.
// All of these are there so all the calculations are done
// and user is given a smoother experience.

// When any change to the map occurs we delay it for any wierd
// bevaviour if user is too fast in zooming or moving the map etc
var MAP_CALLBACKS_DELAY = 1500;

// When map is adjusting itself (e.g, by zooming in or centering to the locality)
// the onChange() is suspended by this constant (because it's not onChange,
// rather it's maps adjustment to bring locality in to the view)
var ENABLE_ON_CHANGE_DELAY = 1600;

// Recalculation happens when any of the change (user interaction to map or search bar)
// occurs. We delay any calculation by this because of setState's async nature
// so any change to the props coming from the wrapper (search-api-wrapper) is not
// in effect for this long
var RECALCULATION_DELAY = 800;

// When map is not availble (map loading from google's servers)
// and user has interacted with the map, this delay makes sure it retries
// every x milliseconds until map is available.
var RETRY_ZOOM_DELAY = 800;

// map button position constants
var LEFT_BOTTOM = 6;
var TOP_LEFT = 1;
var PARLIAMENT_HOUSE_COORDS = {
  lat: -35.308,
  lng: 149.125
};
class MapSearch extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onChange", _ref => {
      var center = _ref.center,
        zoom = _ref.zoom,
        bounds = _ref.bounds;
      if (!this.getMap() || !this.enableOnChange) {
        return;
      }
      var localCenter = mapUtils.toLocalCenter(center);
      var isZoomUpdate = !isEqual(this.currentZoom, zoom);
      var isCenterUpdate = !isEqual(this.currentCenter, localCenter);
      if (isZoomUpdate) {
        this.setState({
          isPending: false
        }, this.debouncedOnMapZoom);
        document.recalculateBounds = false;
      } else if (isCenterUpdate) {
        this.setState({
          isPending: false
        }, this.debouncedOnMapMove);
      }

      // We force map update (for submitting new search) when user changes
      // zoom level
      var forceOnChange = isZoomUpdate;
      this.currentZoom = zoom;
      this.currentCenter = localCenter;
      this.currentBounds = mapUtils.toLocalBounds(bounds);
      this.props.onChange({
        bounds: this.currentBounds,
        zoom: this.currentZoom,
        center: this.currentCenter,
        forceOnChange
      });
    });
    _defineProperty(this, "onMarkerClick", (markerId, markerProps) => {
      if (!this.getMap()) {
        return;
      }
      var evt = {
        id: markerId,
        count: markerProps.count,
        item: markerProps.count > 1 ? markerProps.listings : markerProps.listings[0]
      };

      // We close existing popup and re-open to handle loading of new image
      this.expectEnsurePopupInView = true;
      this.setState({
        openPopup: false
      }, () => {
        this.setState({
          popupProps: {
            lat: markerProps.lat,
            lng: markerProps.lng,
            listings: markerProps.listings
          },
          openPopup: true,
          closePopupOnClick: true
        });
      });
      this.props.onMarkerClick(evt);
    });
    _defineProperty(this, "setGoogleMapsRefs", _ref2 => {
      var map = _ref2.map;
      this._gmap = map;
    });
    _defineProperty(this, "getMap", () => this._gmap);
    _defineProperty(this, "getMapRef", () => this._mapRef);
    _defineProperty(this, "getMapBounds", () => {
      if (!this.getMap()) {
        return null;
      }
      var bounds = this.getMap().getBounds();
      return {
        nw: {
          lat: bounds.getNorthEast().lat(),
          lng: bounds.getSouthWest().lng()
        },
        se: {
          lat: bounds.getSouthWest().lat(),
          lng: bounds.getNorthEast().lng()
        }
      };
    });
    _defineProperty(this, "getMapCenter", () => {
      if (!this.getMap()) {
        return null;
      }
      var center = this.getMap().getCenter();
      return {
        lat: center.lat(),
        lng: center.lng()
      };
    });
    _defineProperty(this, "recalculateBounds", (localBounds, enableOnChangeDelay) => {
      var bounds = localBounds;
      if (!this.getMap()) {
        // We retry to calculate bounds until map object
        // is available. This is useful when first loading the
        // map with available bounds but map is not ready yet
        setTimeout(() => {
          // we surround the retry with this flag to prevent any race conditions
          if (!this.initialZoomCalculated) {
            this.recalculateBounds(bounds, enableOnChangeDelay);
          }
        }, RETRY_ZOOM_DELAY);
        return;
      }
      if (isEqual(bounds.from, bounds.to)) {
        // We only have single pin on the map available,
        // we convert that pin to bounds with default zoom level and
        // 5 and 10 meters NW and SE respectively
        bounds.from = mapUtils.centerToBounds(bounds.from, DEFAULT_ZOOM_LEVEL, 5);
        bounds.to = mapUtils.centerToBounds(bounds.to, DEFAULT_ZOOM_LEVEL, 10);
      }
      var gmapBounds = mapUtils.toGMapBounds(bounds);
      var screenSize = this.getMapRef().getBoundingClientRect();
      var gmapProps = fitBounds(gmapBounds, screenSize);
      if (gmapProps && !Number.isNaN(gmapProps.zoom)) {
        var newZoom = Math.min(Math.max(gmapProps.zoom, AUTO_MIN_ZOOM), AUTO_MAX_ZOOM);
        // When user enters a locality, we zoom in to fit that locality in view
        // while this is happening (zooming and moving etc) we do not want change
        // events to have any affect. Once it's complete we re-enable the onChange
        // events for user interactions
        this.enableOnChange = false;
        this.getMap().setZoom(newZoom);
        this.getMap().panTo(gmapProps.center);
        this.currentCenter = mapUtils.toLocalCenter(this.getMapCenter());
        this.currentBounds = mapUtils.toLocalBounds(this.getMapBounds());
        this.currentZoom = newZoom;
        this.doEnableOnChange(enableOnChangeDelay);
        this.initialZoomCalculated = true;
      }
    });
    _defineProperty(this, "doEnableOnChange", delay => debounce(() => {
      this.enableOnChange = true;
      document.disableOnChange = false;
    }, delay || ENABLE_ON_CHANGE_DELAY)());
    _defineProperty(this, "ensurePopupInView", popupRect => {
      if (!this.expectEnsurePopupInView) {
        return;
      }
      this.enableOnChange = false;
      var mapRect = this.getMapRef().getBoundingClientRect();
      var LENIENCY_MARGIN = 80;
      var offsetY = popupRect.top < mapRect.top + LENIENCY_MARGIN;
      var offsetXLeft = popupRect.left < mapRect.left + LENIENCY_MARGIN;
      var offsetXRight = popupRect.right > mapRect.right - LENIENCY_MARGIN;
      var offsetX = offsetXLeft || offsetXRight;
      if (offsetY || offsetX) {
        var deltaY = offsetY ? LENIENCY_MARGIN + (mapRect.top - popupRect.top) : 0;
        var deltaX = 0;

        // following are the fixes
        var deltaXLeft = popupRect.left - LENIENCY_MARGIN - mapRect.left;
        var deltaXRight = popupRect.right + LENIENCY_MARGIN - mapRect.right;
        if (offsetX) {
          deltaX = offsetXLeft ? deltaXLeft : deltaXRight;
        }

        // finally convert the units from pixel to map meters using current zoom level
        deltaX = mapUtils.pixelToMapMeters(this.currentCenter.lat, this.currentZoom, deltaX);
        deltaY = mapUtils.pixelToMapMeters(this.currentCenter.lat, this.currentZoom, deltaY);
        var newCenter = mapUtils.approximateAddMetersToPoint(this.currentCenter, deltaX, deltaY);
        this.getMap().panTo(mapUtils.toGMapCenter(newCenter));
        this.currentCenter = newCenter;
      }
      this.expectEnsurePopupInView = false;
      this.doEnableOnChange();
    });
    _defineProperty(this, "isInWatchlist", listingIds => this.props.watchlist.some(listingId => listingIds.includes(listingId)));
    _defineProperty(this, "renderMarkers", () => {
      if (this.props.markers) {
        return this.props.markers.map(marker => {
          var listingIds = marker.markerKey.split ? marker.markerKey.split('+').map(listingId => parseInt(listingId, 10)) : [marker.markerKey];
          return ___EmotionJSX(SingleMarker, _extends({}, marker, {
            key: marker.markerKey,
            listingId: marker.markerKey,
            listingIds: listingIds,
            isInWatchlist: this.isInWatchlist(listingIds)
          }));
        });
      }
      return [];
    });
    this.currentZoom = props.zoom;
    this.currentCenter = props.defaultCenter;
    this.currentBounds = props.bounds;
    this.debouncedOnMapZoom = debounce(() => {
      this.setState({
        isPending: false
      }, this.props.onMapZoom);
    }, MAP_CALLBACKS_DELAY);
    this.debouncedOnMapMove = debounce(() => {
      this.setState({
        isPending: false
      }, this.props.onMapMove);
    }, MAP_CALLBACKS_DELAY);
    this.enableOnChange = true;
    this.state = {
      openPopup: false,
      popupProps: null,
      isPending: false
    };
  }
  componentDidMount() {
    if (this.props.bounds) {
      this.recalculateBounds(this.props.bounds, 3000);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    // continueRecalculation is responsible to keep track of any
    // change to the map that was passed in within next 800ms that
    // suggest not to continue the recalculation.
    // This is especially useful when locality is changed to 'map area'
    // which should not re-zoom or re-center the map

    // we read state of recalculateBounds from document object (as it would be)
    // readily updated and available as opposed to nextProps which is subject to
    // setState call. We do not want weird zooms and map moves.
    var recalculateBounds = typeof document !== 'undefined' && document.recalculateBounds;
    this.continueRecalculation = nextProps.bounds && !isEqual(nextProps.bounds, this.currentBounds) && recalculateBounds;
    debounce(() => {
      if (this.continueRecalculation && nextProps.bounds) {
        this.recalculateBounds(nextProps.bounds);
      }
    }, RECALCULATION_DELAY)();
  }
  render() {
    // If we have center available, we use it otherwise default center for
    // map is parliment house. This is edge case when we do not have any listing
    // on the screen when first loaded and use then switches to map view
    var coords = this.props.defaultCenter ? {
      lat: this.props.defaultCenter.lat,
      lng: this.props.defaultCenter.lon
    } : PARLIAMENT_HOUSE_COORDS;
    return ___EmotionJSX("div", {
      css: styles.mapContainer
    }, this.state.isPending && ___EmotionJSX("div", {
      css: styles.mapSpinnerContainer
    }, ___EmotionJSX(Loader, {
      css: styles.mapSpinner
    })), this.props.hiddenProperties > 0 && !this.props.isTruncatedResult && ___EmotionJSX("div", {
      css: styles.mapControlContainer
    }, ___EmotionJSX("div", {
      css: styles.mapMessage
    }, ___EmotionJSX(InfoIcon, {
      css: styles.mapMessageIcon
    }), this.props.hiddenProperties, " propert", this.props.hiddenProperties > 1 ? 'ies ' : 'y ', "not shown for privacy reasons. Switch to List View for all results.")), this.props.isTruncatedResult && ___EmotionJSX("div", null, ___EmotionJSX("div", {
      css: styles.mapZoomMessage
    }, "Too many properties for us to display on the map. Zoom in, or use filters to narrow your search.")), ___EmotionJSX("div", {
      css: styles.map,
      style: {
        height: '720px',
        width: '100%'
      },
      ref: mapRef => {
        this._mapRef = mapRef;
      }
    }, ___EmotionJSX(GoogleMapReact, {
      onGoogleApiLoaded: this.setGoogleMapsRefs,
      bootstrapURLKeys: this.props.bootstrapURLKeys,
      defaultCenter: coords,
      center: coords,
      defaultZoom: this.props.zoom,
      zoom: this.props.zoom,
      onClick: () => {
        if (this.state.closePopupOnClick) {
          this.setState({
            openPopup: false
          });
        }
      },
      onChange: this.onChange,
      onChildClick: this.onMarkerClick,
      draggable: this.state.closePopupOnClick,
      yesIWantToUseGoogleMapApiInternals: true,
      debounced: true,
      options: {
        mapTypeControl: true,
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        zoomControlOptions: {
          position: TOP_LEFT
        },
        mapTypeControlOptions: {
          position: LEFT_BOTTOM
        }
      }
    }, this.renderMarkers(), this.state.openPopup && ___EmotionJSX(PopupWindow, _extends({}, this.state.popupProps, {
      onMouseEnter: () => {
        this.setState({
          closePopupOnClick: false
        });
      },
      onMouseLeave: () => {
        this.setState({
          closePopupOnClick: true
        });
      },
      onAddToWatchlist: this.props.onAddToWatchlist,
      onRender: this.ensurePopupInView,
      listingCategory: this.props.listingCategory
    })))));
  }
}
MapSearch.propTypes = _objectSpread(_objectSpread({}, MAP_SEARCH_PROPS), {}, {
  onMarkerClick: PropTypes.func,
  onMapZoom: PropTypes.func,
  onMapMove: PropTypes.func,
  onAddToWatchlist: PropTypes.func,
  bounds: PropTypes.shape(BOUNDS_PROPS),
  watchlist: PropTypes.arrayOf(PropTypes.number),
  listingCategory: PropTypes.string
});

/* eslint-disable react/default-props-match-prop-types */
MapSearch.defaultProps = {
  baseClass: 'allhomes-listing-search-results',
  zoom: 16,
  markers: [],
  hiddenProperties: 0,
  onChange: () => {},
  onMarkerClick: () => {},
  onMapZoom: () => {},
  onMapMove: () => {},
  onAddToWatchlist: () => {},
  bounds: null,
  watchlist: [],
  listingCategory: ''
};
export default withSendEvent(MapSearch);